import React, { FC, useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import {
  EGeoDataIssueSeverityLevel,
  IDataset,
  IGeoDataIssue,
} from 'FeatureModules/DataImport/types/dataset.types';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { DetailsCard, Badge } from 'FeatureModules/DataImport/Components/DataImport.styles';
import Moment from 'react-moment';
import FlexBox from 'Components/FlexBox';
import PageSubTitle from 'Components/PageSubTitle';

interface IDatasetDetailsCardProps {
  dataset: IDataset;
  buttonText?: string;
  issues: IGeoDataIssue[];
  onClickDataset: (datasetId: string) => void;
  onDownloadDataset: (geoDataId: string) => void;
  onDeleteDataset: (dataset: IDataset) => void;
}

const DatasetDetailsCard: FC<IDatasetDetailsCardProps> = ({
  dataset,
  issues,
  buttonText = 'View',
  onClickDataset,
  onDownloadDataset,
  onDeleteDataset,
}) => {
  const [top, setTop] = useState(0);

  const warnings = issues?.filter(
    issue => issue.severityLevel === EGeoDataIssueSeverityLevel.WARNING
  );

  const errors = issues?.filter(issue => issue.severityLevel === EGeoDataIssueSeverityLevel.ERROR);

  const calculateTop = () => {
    const scrollTop = Math.max((document.scrollingElement?.scrollTop || 0) - 246, 0);
    setTop(scrollTop);
  };

  useEffect(() => {
    calculateTop();
  });

  useEffect(() => {
    document.addEventListener('scroll', calculateTop);
    return () => {
      document.removeEventListener('scroll', calculateTop);
    };
  });

  return (
    <Box position="relative" minWidth={395} width="20%">
      <DetailsCard top={top} data-testid="dataset-details-card">
        <PageSubTitle title="Details" />

        <FlexBox gap={1}>
          <ThemeTypography variant="BODY_MEDIUM_BOLD">{dataset.title || 'Unnamed'}</ThemeTypography>
          {!!errors.length && (
            <Box my={1}>
              <Badge severity="error">
                {errors.length} error
                {errors.length && errors.length > 1 ? 's' : ''}
              </Badge>
            </Box>
          )}
          {!!warnings.length && (
            <Box my={1}>
              <Badge severity="warning">
                {warnings.length} warning
                {warnings.length && warnings.length > 1 ? 's' : ''}
              </Badge>
            </Box>
          )}
        </FlexBox>

        <FlexBox gap={1} mb={2}>
          <ThemeTypography variant="BODY_MEDIUM" autoOverflow>
            Created <Moment format="LL">{dataset.createdTimestamp}</Moment>
          </ThemeTypography>
          {dataset.ownedBy && (
            <>
              <ThemeTypography variant="BODY_MEDIUM">|</ThemeTypography>
              <ThemeTypography variant="BODY_MEDIUM" autoOverflow>
                Owned by {dataset.ownedBy.name}
              </ThemeTypography>
            </>
          )}
        </FlexBox>
        <FlexBox flexWrap="wrap" gap={2}>
          <ThemeButton
            color="BLUE_ICE"
            startIcon={<Icon name="show" />}
            onClick={() => onClickDataset(dataset.id)}
          >
            {buttonText}
          </ThemeButton>
          <ThemeButton
            color="BLUE_ICE"
            startIcon={<Icon name="download" />}
            onClick={() => onDownloadDataset(dataset.originalGeoDataId)}
          >
            Download geo-data
          </ThemeButton>
          <Tooltip
            title={
              !dataset.isDeletable
                ? 'This item is owned by another company. Accordingly, it can only be deleted by its owner.'
                : ''
            }
            placement="top"
          >
            <span>
              <ThemeButton
                color="BLUE_ICE"
                startIcon={<Icon name="delete" />}
                onClick={() => onDeleteDataset(dataset)}
                data-testid="delete-dataset-button"
                disabled={!dataset.isDeletable}
              >
                Delete
              </ThemeButton>
            </span>
          </Tooltip>
        </FlexBox>
      </DetailsCard>
    </Box>
  );
};

export default DatasetDetailsCard;
